import { PlayArrow } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Box, Button, Drawer, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PassageDrawerProps } from '../../../@types/ContextProviderRelated';
import { EUnitType } from '../../../@types/Enums';
import { useAttributeActions } from '../../../hooks/useAttributeActions';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { getDefaultObjectId, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { deleteButton, immersightBlue } from '../../../utils/colors';
import { MIN_PASSAGE_LENGTH, MIN_PASSAGE_LENGTH_IMPERIAL } from '../../../utils/constants';
import { cmToInternalRounded, getLocalizedUnitLabel, inchToInternalRounded, internalToCentimetersRounded, internalToInchesRounded } from '../../../utils/conversionUtils';
import filesApiService from '../../../utils/filesApiService';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';

const PassageDrawer = ({ open, onClose, selectedPassage, resetSelection, id }: PassageDrawerProps): JSX.Element => {
  const { building, updatePassageWidth, deletePassage, updatePassageAttributes, defaultObjects } = useBuilding();
  const wallIndex = selectedPassage.wallIndex;
  const passageIndex = selectedPassage.passageIndex;
  const wall = building.walls[wallIndex] as IWall;
  const passage = wall?.passages?.[passageIndex];
  const { unitType } = useUnitContext();
  const initialPassageWidth = passage?.length ? internalToCentimetersRounded(passage.length) : MIN_PASSAGE_LENGTH;
  const initialPassageWidthImperial = passage?.length ? internalToInchesRounded(passage.length) : MIN_PASSAGE_LENGTH_IMPERIAL;
  const [passageWidth, setPassageWidth] = useState<number>(unitType === EUnitType.METRIC ? initialPassageWidth : initialPassageWidthImperial);
  const intl = useIntl();
  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'centimeter'), [intl]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'inch'), [intl]);
  const wallLengthMetric = useMemo(() => (wall ? Math.max(1, internalToCentimetersRounded(wall.startPoint.distanceTo(wall.endPoint))) : MIN_PASSAGE_LENGTH), [wall]);
  const wallLengthImperial = useMemo(() => (wall ? Math.max(1, internalToInchesRounded(wall.startPoint.distanceTo(wall.endPoint))) : MIN_PASSAGE_LENGTH_IMPERIAL), [wall]);
  const maxPassageWidth = unitType === EUnitType.METRIC ? wallLengthMetric : wallLengthImperial;
  const minPassageWidth = unitType === EUnitType.METRIC ? Math.min(MIN_PASSAGE_LENGTH, wallLengthMetric) : Math.min(MIN_PASSAGE_LENGTH_IMPERIAL, wallLengthImperial);

  const [attributes, setAttributes] = useState<AttributeModel[]>(passage?.attributes ?? []);
  const [attributeValues, setAttributeValues] = useState<Record<number, string>>(
    (passage?.attributes ?? []).reduce(
      (acc, a) => {
        if (a.value !== undefined && a.value !== null) acc[a.id] = `${a.value}`;
        return acc;
      },
      {} as Record<number, string>,
    ),
  );
  const [showInputFields, setShowInputFields] = useState<Record<number, boolean>>(
    (passage?.attributes ?? []).reduce(
      (acc, a) => {
        acc[a.id] = a.value !== undefined && a.value !== null;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );
  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [attrSelected, setAttrSelected] = useState<IAttribute | null>(null);
  const [attrInputValue, setAttrInputValue] = useState('');
  const [attrError, setAttrError] = useState<string | null>(null);
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const { handleAttributeValueChange, handleDeleteAttribute, toggleInputField, handleAttributeSelection, getSortedAttributes } = useAttributeActions(
    attributes,
    setAttributes,
    setAttributeValues,
    setShowInputFields,
    setAttrSelected,
    setAttrInputValue,
    (attrs) => updatePassageAttributes(wallIndex, passageIndex, attrs),
    allAttributes,
    setAllAttributes,
    setAttrError,
  );

  const localizedPassageName = useMemo(() => {
    const objId = getDefaultObjectId(ObjectType.Passage);
    const apiObj = defaultObjects?.find((o: any) => o.id === objId);
    const { locale } = intl;

    if (apiObj && apiObj.localization) {
      const currentLocale = locale.split('-')[0];
      return apiObj.localization[currentLocale] || apiObj.localization.en || apiObj.name;
    }

    return intl.formatMessage({ id: 'PassageDrawer.passage', defaultMessage: 'Durchgang' });
  }, [defaultObjects, intl]);

  const handleAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      const updated = attributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit,
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );
      setAttributes(updated);
      updatePassageAttributes(wallIndex, passageIndex, updated);
    },
    [attributes, updatePassageAttributes, wallIndex, passageIndex],
  );

  useEffect(() => {
    if (wall && passage) {
      setPassageWidth(
        unitType === EUnitType.METRIC
          ? passage.length
            ? internalToCentimetersRounded(passage.length)
            : MIN_PASSAGE_LENGTH
          : passage.length
            ? internalToInchesRounded(passage.length)
            : MIN_PASSAGE_LENGTH_IMPERIAL,
      );
      setAttributes(passage.attributes ?? []);
      setAttributeValues(
        (passage.attributes ?? []).reduce(
          (acc, a) => {
            if (a.value !== undefined && a.value !== null) acc[a.id] = `${a.value}`;
            return acc;
          },
          {} as Record<number, string>,
        ),
      );
      setShowInputFields(
        (passage.attributes ?? []).reduce(
          (acc, a) => {
            acc[a.id] = a.value !== undefined && a.value !== null;
            return acc;
          },
          {} as Record<number, boolean>,
        ),
      );
    } else {
      onClose();
    }
  }, [passageIndex, wallIndex, unitType, passage, wall, onClose]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const catalog = await filesApiService.getAttributes(regionCode);
        setAllAttributes(catalog);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error fetching attributes: ${(e as Error).message}`);
      }
    })();
  }, [open, regionCode]);

  const validateAndSet = useCallback(
    (value: number): void => {
      if (!isNaN(value)) {
        if (value < minPassageWidth) {
          value = minPassageWidth;
        } else if (value > maxPassageWidth) {
          value = maxPassageWidth;
        }
        const roundedInternalValue = unitType === EUnitType.METRIC ? cmToInternalRounded(value) : inchToInternalRounded(value);
        setPassageWidth(value);
        updatePassageWidth(wallIndex, passageIndex, roundedInternalValue);
      } else {
        setPassageWidth(unitType === EUnitType.METRIC ? initialPassageWidth : initialPassageWidthImperial);
      }
    },
    [unitType, minPassageWidth, maxPassageWidth, initialPassageWidth, initialPassageWidthImperial, updatePassageWidth, wallIndex, passageIndex],
  );

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value, 10);
    if (!isNaN(newValue)) {
      setPassageWidth(newValue);
    }
  }, []);

  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const value = parseInt(event.target.value, 10);
      validateAndSet(value);
    },
    [validateAndSet],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      const value = parseInt((event.target as HTMLInputElement).value, 10);
      if (event.key === 'Enter') {
        validateAndSet(value);
      } else if (!isNaN(value)) {
        setPassageWidth(value);
      }
    },
    [validateAndSet],
  );

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [onClose, resetSelection]);

  const handleOnDelete = useCallback(() => {
    deletePassage(wallIndex, passageIndex);
    resetSelection();
    onClose();
  }, [wallIndex, passageIndex, deletePassage, resetSelection, onClose]);

  const wheelConfig: WheelInputConfig = {
    unitType,
    minMetric: Math.min(MIN_PASSAGE_LENGTH, wallLengthMetric),
    maxMetric: wallLengthMetric,
    minInches: Math.min(MIN_PASSAGE_LENGTH_IMPERIAL, wallLengthImperial),
    maxInches: wallLengthImperial,
  };

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: '64px',
          zIndex: 1000,
          width: '420px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              {localizedPassageName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container item direction="column" spacing={3} p={3}>
        <Grid item>
          <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
            <FormattedMessage
              id="PassageDrawer.passageWidth"
              defaultMessage="Passage Width in {unit}"
              values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }}
            />
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={passageWidth}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyUp={handleKeyPress}
            onWheel={(e) =>
              handleWheelInput(
                e,
                passageWidth.toString(),
                (v: string) => {
                  const newVal = parseInt(v, 10);
                  if (!isNaN(newVal)) {
                    setPassageWidth(newVal);
                  }
                },
                wheelConfig,
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SyncAltIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: minPassageWidth,
                max: maxPassageWidth,
              },
            }}
          />
        </Grid>

        <Grid item>
          <AttributeList
            attributes={getSortedAttributes(attributes)}
            attributeValues={attributeValues}
            showInputFields={showInputFields}
            onAttributeValueChange={handleAttributeValueChange}
            onAttributeUnitChange={handleAttributeUnitChange}
            onDeleteAttribute={handleDeleteAttribute}
            onToggleInputField={toggleInputField}
            onReorderAttributes={(newAttrs) => {
              setAttributes(newAttrs);
              updatePassageAttributes(wallIndex, passageIndex, newAttrs);
            }}
          />
        </Grid>
        <Grid item>
          <Box
            sx={{
              mt: -5,
              mb: 1,
              width: '99%',
              mx: 'auto',
            }}
          >
            <AttributeAutocomplete
              attributes={allAttributes}
              disabledAttributeIds={attributes.map((attribute) => attribute.id)}
              selected={attrSelected}
              inputValue={attrInputValue}
              error={attrError}
              onChange={handleAttributeSelection}
              onInputChange={(val) => {
                setAttrInputValue(val);
                if (val !== attrSelected?.key) setAttrSelected(null);
              }}
              placeholder={intl.formatMessage({ id: 'PassageDrawer.addAttribute', defaultMessage: 'Add attribute' })}
            />
          </Box>
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleOnDelete}
            sx={{
              backgroundColor: deleteButton,
              marginTop: '20px',
            }}
          >
            <FormattedMessage id="PassageDrawer.deletePassage" defaultMessage="Delete Passage" />
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default PassageDrawer;
